import { useFeatureFlag } from '@zep/feature-flag';

import type { BoardTemplate } from '../types/template';

/**
 * 보드 생성 시 기록하는 안내문 배정.
 *
 * boolean 플래그는 값이 둘인데 상황은 셋이다 — `enabled: false` 가 "대조군 배정"과
 * "WASM 이 아직 안 뜸"을 똑같이 만든다. 그대로 기록하면 Flipt 장애 구간이 통째로
 * 대조군으로 집계돼 배정 비율(SRR)이 깨지는데 원인을 알 길이 없다. isReady 를 섞어
 * `unassigned` 를 떼어내면 그 비율만 세어 실험 유효성을 직접 판정할 수 있다.
 *
 * `unassigned` 도 문구는 A안으로 만든다(안전한 기본값). 로그만 갈라 두면 분석할 때
 * 대조군에 합칠지 제외할지 나중에 고를 수 있다.
 */
export type BoardNoticeVariant = 'current' | 'narrowed' | 'unassigned';

/**
 * 안내문 A/B 배정을 읽는다.
 *
 * 오너 단위로 고정한다(`bucketBy: 'user'`) — 문구는 보드마다 다르면 안 되고,
 * 같은 선생님이 만든 보드는 같은 안을 받아야 한 군으로 묶인다.
 *
 * ⚠️ 배정은 렌더 중에 평가되고, 보드를 만드는 순간의 값이 그대로 DB 에 평문으로
 * 저장된다(templateToCreateBoardReq). 저장 후에는 플래그를 꺼도 되돌아가지 않는다.
 */
export function useBoardNoticeVariant(): {
  variant: BoardNoticeVariant;
  /**
   * 배정에 맞는 안내문으로 갈아끼운 템플릿. 생성 payload 를 만드는 쪽은 A/B 를 모르고,
   * 이 템플릿을 그대로 쓰면 된다 — 저장되는 문구와 트래킹의 배정이 한 출처에서 나온다.
   *
   * B안이 없는 템플릿(원문이 이미 구체적인 작품 공유·칭찬 게시판)은 A안 그대로 둔다.
   */
  applyNoticeVariant: (template: BoardTemplate) => BoardTemplate;
} {
  const { enabled, isReady } = useFeatureFlag(
    'quiz_board_template_notice_narrowed',
    { bucketBy: 'user' },
  );

  const variant: BoardNoticeVariant = !isReady
    ? 'unassigned'
    : enabled
    ? 'narrowed'
    : 'current';

  return {
    variant,
    applyNoticeVariant: template =>
      variant === 'narrowed' && template.noticeKeyB
        ? { ...template, noticeKey: template.noticeKeyB }
        : template,
  };
}
